/* @jsx h */
import { defineComponent, ref, onMounted, h, computed } from "vue";
import { api } from "@app/data/index";
import { BackendAPICartItem, Order, TableStatus } from "@lemmonhq/types";
import { Loading, LemmonModal } from "@app/vue/components/index";
import { useToast } from "@app/vue/providers/Toast";
import { sortTables, formatPrice } from "@lemmonhq/orders";
import { PartialPaymentContent } from "./payments/PartialPaymentContent";
import { useTrpc, useShop } from "@app/vue/providers/index";
import { useMutation } from "@tanstack/vue-query";
import { useAuth } from "@app/vue/providers/Auth";

interface TransferItemsToTableModalProps {
  currentTable: TableStatus;
  onClose: () => void;
  onSuccess: () => void;
}

export const TransferItemsToTableModal =
  defineComponent<TransferItemsToTableModalProps>({
    name: "ReassignItemsModal",
    props: ["currentTable", "onClose", "onSuccess"],
    setup(props) {
      const auth = useAuth();
      const tables = ref<TableStatus[]>([]);
      const orders = ref<Order[]>([]);
      const loading = ref(true);
      const selectedTableId = ref<number | null>(null);
      const selectedItems = ref<Record<number, number>>({});
      const expandedGroups = ref<Set<string>>(new Set());
      const forceTransfer = ref(false);
      const targetTableHasOrders = ref(false);
      const toast = useToast();
      const trpc = useTrpc();
      const shop = useShop();

      // Compute if user has confirmed awareness of target table having orders
      const showForceWarning = computed(() => {
        return targetTableHasOrders.value && !forceTransfer.value;
      });

      // Compute total of selected items
      const selectedTotal = computed(() => {
        let total = 0;
        orders.value.forEach((order) => {
          (order.items || []).forEach((item) => {
            const qty = selectedItems.value[item.id] || 0;
            if (qty > 0) {
              total += parseFloat(item.price as any) * qty;
            }
          });
        });
        return total;
      });

      // Compute count of selected items
      const selectedCount = computed(() => {
        return Object.values(selectedItems.value).reduce(
          (sum, qty) => sum + qty,
          0,
        );
      });

      // Helper function to normalize entity_type (e.g., "App\\Models\\Extra" -> "extras")
      const normalizeEntityType = (entityType: string): string => {
        // Extract the model name from the full class path
        const modelName = entityType.split("\\").pop() || entityType;
        // Convert to lowercase and pluralize
        return modelName.toLowerCase() + "s";
      };

      // Helper function to generate UUID
      const generateUUID = (): string => {
        return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
          /[xy]/g,
          function (c) {
            const r = (Math.random() * 16) | 0;
            const v = c === "x" ? r : (r & 0x3) | 0x8;
            return v.toString(16);
          },
        );
      };

      // Transfer items mutation
      const transferMutation = useMutation({
        mutationFn: async () => {
          if (!selectedTableId.value) {
            throw new Error("Please select a target table");
          }

          if (selectedCount.value === 0) {
            throw new Error("Please select at least one item to transfer");
          }
          // Build the items array for transfer
          const itemsToTransfer: Array<{
            item: BackendAPICartItem;
            order_item: {
              id: number;
              qty: number;
            };
          }> = [];

          orders.value.forEach((order) => {
            (order.items || []).forEach((orderItem) => {
              const qty = selectedItems.value[orderItem.id];
              if (qty && qty > 0) {
                // Map OrderItemBundle[] to BackendAPICartItemBundleOption[].
                // NOTE: the previous version sent `bundle_id: bundle.id`, i.e.
                // the order_item_bundles row PK, not the catalog bundle id —
                // corrupting bundle identity on every transfer. It now reads the
                // snapshot columns. Legacy rows predating the bundle-snapshot
                // migration have NULL snapshots, so they fall back to 0 /
                // "Unavailable" and must be backfilled — see
                // docs/open_bugs/legacy_order_item_bundle_snapshots_missing.md
                const bundles = (orderItem.bundles || []).map((bundle) => ({
                  bundle_id: bundle.bundle_id ?? 0,
                  bundle_name: bundle.bundle_name ?? "Unavailable",
                  option_id: bundle.option_id ?? 0,
                  option_name: bundle.option_name ?? "Unavailable",
                  entity_type: normalizeEntityType(bundle.entity_type),
                  entity_id: bundle.entity_id,
                  entity_name: bundle.entity_name ?? "Unavailable",
                  quantity_per_parent: bundle.quantity_per_parent ?? 1,
                  price: bundle.price,
                  snapshot_status: bundle.snapshot_status ?? "unavailable",
                }));

                // Calculate bundle total
                const bundle_total = bundles.reduce(
                  (sum, bundle) =>
                    sum + bundle.price * bundle.quantity_per_parent,
                  0,
                );

                itemsToTransfer.push({
                  item: {
                    id: orderItem.id,
                    product_id: orderItem.product.id,
                    quantity: qty,
                    cart_id: generateUUID(),
                    type: "restaurant",
                    price: String(orderItem.price),
                    food_type_id: orderItem.food_type_id,
                    bundle: bundles,
                    bundle_total: bundle_total,
                  },
                  order_item: {
                    id: orderItem.id,
                    qty: qty,
                  },
                });
              }
            });
          });

          return trpc.orders.transferItemsToTable.mutate({
            source_table_id: props.currentTable.table.id,
            target_table_id: selectedTableId.value,
            items: itemsToTransfer,
            force: forceTransfer.value,
          });
        },
        onSuccess: async (data) => {
          // Process order creation with print jobs
          try {
            await api.processOrderCreation(shop, {
              order: data.order,
              printJobs: data.printJobs,
            });
          } catch {}

          toast.showToast(
            `Items transferred successfully to new order #${data.order_id}`,
            {
              type: "success",
              autoHide: 3000,
              icon: "check_circle",
            },
          );
          props.onSuccess();
        },
        onError: (error: any) => {
          console.error("Failed to transfer items:", error);

          // Extract error message properly
          const errorMessage =
            error?.message ||
            error?.data?.message ||
            error?.error?.message ||
            (typeof error === "string" ? error : "Failed to transfer items");

          console.log("Error message:", errorMessage);

          // Check if it's the "target table has orders" error
          if (
            errorMessage?.includes("Target table already has active orders") ||
            errorMessage?.includes("p_force=true")
          ) {
            targetTableHasOrders.value = true;
            toast.showToast(
              "Target table has active orders. Please confirm to merge orders.",
              {
                type: "warning",
                autoHide: 5000,
                icon: "warning",
              },
            );
          } else {
            toast.showToast(errorMessage, {
              type: "error",
              autoHide: 5000,
              icon: "error",
            });
          }
        },
      });

      onMounted(async () => {
        try {
          // Fetch available tables
          const tablesData = await trpc.tables.list.query();
          tables.value = tablesData.filter(
            (table) => table.table.id !== props.currentTable.table.id,
          );

          // Fetch orders for current table
          const orderIds = props.currentTable.orders.map((order) => order.id);
          if (orderIds.length > 0) {
            orders.value = await api.getOrders(orderIds);
          }
        } catch (error) {
          console.error("Failed to load data:", error);
          toast.showToast("Failed to load data", { type: "error" });
        } finally {
          loading.value = false;
        }
      });

      const handleSelectItem = (itemId: number, quantity: number) => {
        if (quantity > 0) {
          selectedItems.value[itemId] = quantity;
        } else {
          delete selectedItems.value[itemId];
        }
        // Force reactivity
        selectedItems.value = { ...selectedItems.value };
      };

      const handleTableChange = (tableId: number) => {
        selectedTableId.value = tableId;
        forceTransfer.value = false;
        targetTableHasOrders.value = false;

        // Check if selected table has orders
        const selectedTable = tables.value.find((t) => t.table.id === tableId);
        if (selectedTable && selectedTable.orders.length > 0) {
          targetTableHasOrders.value = true;
        }
      };

      const handleSubmit = () => {
        transferMutation.mutate();
      };
      onMounted(() => {
        const element = document.activeElement;
        if (element && element instanceof HTMLElement) element.blur();
      });
      return () => (
        <LemmonModal
          title="Reassign Items to Another Table"
          onClose={props.onClose}
          renderButtons={() => (
            <div class="p-4 border-t ">
              <div class="flex justify-between items-center gap-4">
                <div class="flex-1 text-left">
                  {selectedCount.value > 0 && (
                    <div class="text-sm ">
                      <div>Selected: {selectedCount.value} items</div>
                      <div class="text-accent font-bold">
                        Total:{" "}
                        {formatPrice(
                          selectedTotal.value.toString(),
                          true,
                          2,
                          auth.user.value?.restaurant.currency || "CHF",
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div class="flex gap-2">
                  <button
                    onClick={props.onClose}
                    class="btn btn-neutral"
                    disabled={transferMutation.isPending.value}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmit}
                    class="btn btn-primary"
                    disabled={
                      !selectedTableId.value ||
                      selectedCount.value === 0 ||
                      transferMutation.isPending.value ||
                      showForceWarning.value
                    }
                  >
                    {transferMutation.isPending.value ? (
                      <span class="loading loading-spinner loading-sm"></span>
                    ) : forceTransfer.value ? (
                      "Force Transfer"
                    ) : (
                      "Transfer Items"
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        >
          <div class="flex flex-col h-full overflow-hidden ">
            {loading.value ? (
              <div class="flex justify-center p-6 min-h-48 items-center">
                <Loading />
              </div>
            ) : (
              <>
                {/* Table Selection */}
                <div class="p-4 border-b  space-y-4">
                  <div>
                    <label class="block text-sm font-medium  mb-2">
                      Select Target Table
                    </label>
                    <select
                      autofocus={true}
                      class="select select-bordered w-full bg-gray-900 text-accent"
                      value={selectedTableId.value || ""}
                      onChange={(e) => {
                        handleTableChange(
                          Number((e.target as HTMLSelectElement).value),
                        );
                      }}
                    >
                      <option value="">Select a table...</option>
                      {sortTables(tables.value).map((table) => (
                        <option key={table.table.id} value={table.table.id}>
                          {table.table.name}
                          {table.table.is_busy ? " (Busy)" : " (Free)"}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Warning for target table with orders */}
                  {targetTableHasOrders.value && (
                    <div class="alert alert-neutral flex l-box-shadow p-3">
                      <div class="flex flex-1 flex-row items-center gap-3 w-full">
                        <span class="material-symbols-outlined text-warning">
                          warning
                        </span>
                        <div class="flex-1 text-sm text-accent">
                          Target table has existing orders. Items will be merged
                          into a new order.
                        </div>
                        <label class="flex items-center gap-2 cursor-pointer whitespace-nowrap">
                          <span class="text-sm font-medium">Confirm</span>
                          <input
                            type="checkbox"
                            class="toggle toggle-warning"
                            checked={forceTransfer.value}
                            onChange={(e) => {
                              forceTransfer.value = (
                                e.target as HTMLInputElement
                              ).checked;
                            }}
                          />
                        </label>
                      </div>
                    </div>
                  )}
                </div>

                {/* Items Selection */}
                <div class="flex flex-col flex-1 overflow-hidden">
                  {orders.value.length === 0 ? (
                    <div class="p-6 text-center ">
                      No items available to transfer
                    </div>
                  ) : (
                    <>
                      <div class="p-4 border-b  hidden">
                        <h3 class="text-lg font-medium">
                          Select Items to Transfer
                        </h3>
                        <p class="text-sm ">
                          Choose which items to move to the target table
                        </p>
                      </div>
                      <PartialPaymentContent
                        orders={orders.value}
                        partialPayments={[]}
                        selectedItems={selectedItems.value}
                        onSelectItem={handleSelectItem}
                        expandedGroups={expandedGroups}
                      />
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </LemmonModal>
      );
    },
  });
